import React, { useState, useEffect } from "react";
import { Download, X, Share } from "lucide-react";

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode
    const inStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    setIsStandalone(inStandalone);
    if (inStandalone) return;

    // Check if dismissed recently (within 48 hours)
    const dismissedTime = localStorage.getItem("afriport_pwa_dismissed");
    if (dismissedTime && Date.now() - parseInt(dismissedTime, 10) < 48 * 60 * 60 * 1000) {
      return;
    }

    // Check for iOS Safari
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);

    if (isIosDevice && isSafari && !inStandalone) {
      setIsIOS(true);
      // Wait 3 seconds before showing iOS prompt so user is not immediately interrupted
      const timer = setTimeout(() => setShowPrompt(true), 3500);
      return () => clearTimeout(timer);
    }

    // Android / Chromium
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => setShowPrompt(true), 2500);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("afriport_pwa_dismissed", Date.now().toString());
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 24,
        left: 20,
        right: 20,
        maxWidth: 440,
        margin: "0 auto",
        background: "linear-gradient(135deg, #0D1B2A 0%, #162436 100%)",
        color: "#fff",
        borderRadius: "var(--radius-lg, 16px)",
        padding: "16px 18px",
        boxShadow: "0 12px 36px rgba(13,27,42,0.35), 0 0 0 1px rgba(255,255,255,0.08)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        animation: "slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
        <img
          src="/images/Afriportstore.png"
          alt="Afriport Store"
          style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 10, background: "rgba(255,255,255,0.06)", padding: 4, flexShrink: 0 }}
        />
        <div style={{ minWidth: 0 }}>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>Install Afriport App</h4>
          <p style={{ margin: "3px 0 0", fontSize: 12, color: "#94A3B8", lineHeight: 1.35 }}>
            {isIOS
              ? "Tap Share ⎋ then 'Add to Home Screen'"
              : "Shop faster with offline support & quick access"}
          </p>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstallClick}
            className="btn btn-primary btn-sm"
            style={{
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 700,
              gap: 6,
              borderRadius: "var(--radius-full, 9999px)",
              background: "var(--brand, #E8471A)",
              border: "none",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <Download size={14} /> Install
          </button>
        )}
        {isIOS && (
          <span style={{ fontSize: 11, background: "rgba(255,255,255,0.1)", padding: "4px 8px", borderRadius: 6, color: "#E2E8F0", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Share size={12} /> Share
          </span>
        )}
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install banner"
          style={{
            border: "none",
            background: "transparent",
            color: "#94A3B8",
            cursor: "pointer",
            padding: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 6,
          }}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
